/**
 * Geometry of the Target session's pace & dispersion plot
 * (components/PaceDispersionSummary.tsx PaceDispersionPlot): the disc, goal
 * ring, 80% ellipse, dots, centroid cross, and axis labels in pixels for a
 * square plot of `size`. Pure; the phone plot and both TV receivers draw from
 * it. Moved out of PaceDispersionSummary unchanged.
 */

/** The plot's inputs, a subset of utils/paceDispersion's summary that a TV
 *  can receive as-is. */
export interface PacePlotInput {
  points: ReadonlyArray<{ lateralMissFraction: number; paceMissFraction: number; isMake: boolean }>;
  /** Goal ring radius as a fraction of target distance (0.10 = 10%). */
  goalFraction: number;
  ellipse: {
    center: { lateralFraction: number; paceFraction: number };
    semiMajorFraction: number;
    semiMinorFraction: number;
    rotationDeg: number;
  } | null;
  centroid: { lateralFraction: number; paceFraction: number } | null;
}

/** The goal stepper's ceiling; the scale never shrinks below it so moving the
 *  goal changes the ring, not every dot. */
export const PACE_PLOT_MAX_GOAL_FRACTION = 0.2;

export interface PacePlotGeometry {
  center: number;
  plotRadius: number;
  scale: number;
  goalRadius: number;
  ellipse: { cx: number; cy: number; rx: number; ry: number; rotationDeg: number } | null;
  points: Array<{ x: number; y: number; isMake: boolean; first: boolean }>;
  centroid: { x: number; y: number } | null;
  labelSize: number;
  pointRadius: number;
  labels: {
    long: { x: number; y: number };
    short: { x: number; y: number };
    left: { x: number; y: number };
    right: { x: number; y: number };
  };
}

export function pacePlotGeometry(input: PacePlotInput, size: number): PacePlotGeometry {
  const center = size / 2;
  const plotRadius = Math.max(size / 2 - 29, 20);
  const { ellipse } = input;
  // Radial bounds keep diagonal leaves contained; the scale is independent of
  // the selected goal.
  const maxPointRadius = input.points.reduce(
    (max, point) => Math.max(max, Math.hypot(point.lateralMissFraction, point.paceMissFraction)),
    0,
  );
  const ellipseExtent = ellipse
    ? Math.hypot(ellipse.center.lateralFraction, ellipse.center.paceFraction) + ellipse.semiMajorFraction
    : 0;
  const dataExtent = Math.max(PACE_PLOT_MAX_GOAL_FRACTION, maxPointRadius, ellipseExtent, 0.025);
  const scale = plotRadius / (dataExtent * 1.08);
  const mapX = (fraction: number) => center + fraction * scale;
  const mapY = (fraction: number) => center - fraction * scale;
  const labelSize = Math.max(9, Math.min(13, size * 0.04));
  return {
    center,
    plotRadius,
    scale,
    goalRadius: input.goalFraction * scale,
    ellipse: ellipse
      ? {
          cx: mapX(ellipse.center.lateralFraction),
          cy: mapY(ellipse.center.paceFraction),
          rx: ellipse.semiMajorFraction * scale,
          ry: ellipse.semiMinorFraction * scale,
          rotationDeg: ellipse.rotationDeg,
        }
      : null,
    points: input.points.map((point, index) => ({
      x: mapX(point.lateralMissFraction),
      y: mapY(point.paceMissFraction),
      isMake: point.isMake,
      first: index === 0,
    })),
    centroid: input.centroid
      ? { x: mapX(input.centroid.lateralFraction), y: mapY(input.centroid.paceFraction) }
      : null,
    labelSize,
    pointRadius: Math.max(3.5, Math.min(6, size * 0.018)),
    labels: {
      long: { x: center, y: 15 },
      short: { x: center, y: size - 5 },
      left: { x: 4, y: center + labelSize / 3 },
      right: { x: size - 4, y: center + labelSize / 3 },
    },
  };
}
